import { ensureSyntaxTree, syntaxTreeAvailable } from "@codemirror/language";
import {
  EditorSelection,
  EditorState,
  Range,
  RangeSet,
  RangeSetBuilder,
  SelectionRange,
  StateField,
  Transaction,
} from "@codemirror/state";
import {
  Decoration,
  EditorView,
  layer,
  lineNumbers,
  RectangleMarker,
  ViewPlugin,
  ViewUpdate,
  WidgetType,
  type DecorationSet,
} from "@codemirror/view";
import { heynoteEvent, LANGUAGE_CHANGE } from "../annotation";
import type { MultiBlockEditor } from "../editor";
import { SelectionChangeEvent } from "../event";
import type { SimpleRange } from "../types";
import { firstBlockDelimiterSize, getBlocksFromString, getBlocksFromSyntaxTree, getBlocksFromSyntaxTreeNewParser, useNewParserFacet } from "./block-parsing";
import { mathBlock } from "./math";
import { emptyBlockSelected } from "./select-all";

export const delimiterRegex = /^\n∞∞∞[a-z]+?(-a)?\n$/;
export const delimiterRegexWithoutNewline = /^∞∞∞[a-z]+?(-a)?$/;

/**
 * Get the blocks from the document state.
 * If the syntax tree is available, we'll extract the blocks from that. Otherwise
 * the blocks are parsed from the string contents of the document, which is much faster
 * than waiting for the tree parsing to finish.
 */
export function getBlocks(state: EditorState): Block[] {
  if (syntaxTreeAvailable(state, state.doc.length)) {
    if (state.facet(useNewParserFacet)) {
      return getBlocksFromSyntaxTreeNewParser(state);
    }
    return getBlocksFromSyntaxTree(state);
  } else {
    const doc = state.doc;
    const s = doc.sliceString(0, doc.length);
    return getBlocksFromString(s);
  }
}

export const blockState = StateField.define({
  create(state) {
    return getBlocks(state);
  },
  update(blocks, transaction) {
    // if blocks are empty it likely means we didn't get a parsed syntax tree, and then we want to update
    // the blocks on all updates (and not just document changes)
    if (transaction.docChanged || blocks.length === 0) {
      return getBlocks(transaction.state);
    }
    return blocks;
  },
});

function findBlockWithPos(blocks: Block[], pos: number): Block | undefined {
  for (let block of blocks) {
    if (block.from <= pos && block.to >= pos) {
      return block;
    }
  }
  return undefined;
}

function findBlocNokWithPos(blocks: Block[], pos: number): number {
  let no = 0;
  for (let block of blocks) {
    if (block.from <= pos && block.to >= pos) {
      return no;
    }
    no++;
  }
  return -1;
}

export function getActiveNoteBlock(state: EditorState): Block | undefined {
  // find which block the cursor is in
  const range = state.selection.asSingle().ranges[0] as SelectionRange;
  let blocks = state.field(blockState);
  return findBlockWithPos(blocks, range.head);
}

// layout: [delimiter][content]
// delimiter: {from, contentFrom}
// content: {contentFrom, to}
// full block: {from, to}
export interface Block {
  index: number;
  from: number;
  to: number;
  contentFrom: number;
  language: string;
  autoDetect: boolean;
}

export function blockDelimiter(b: Block): SimpleRange {
  return { from: b.from, to: b.contentFrom };
}
export function blockContent(b: Block): SimpleRange {
  return { from: b.contentFrom, to: b.to };
}
export function blockRange(b: Block): SimpleRange {
  return { from: b.from, to: b.to };
}

export interface BlocksInfo {
  blocks: Block[];
  active: number;
}

export function getBlocksInfo(state: EditorState): BlocksInfo {
  // find which block the cursor is in
  const range = state.selection.asSingle().main;
  let blocks = state.field(blockState);
  let active = findBlocNokWithPos(blocks, range.head);
  if (active < 0) {
    active = 0;
  }
  return {
    blocks: blocks,
    active: active,
  };
}

export function getFirstNoteBlock(state: EditorState): Block {
  let blocks = state.field(blockState);
  return blocks[0] as Block;
}

export function getLastNoteBlock(state: EditorState): Block {
  let blocks = state.field(blockState);
  return blocks[blocks.length - 1] as Block;
}

export function getBlockN(state: EditorState, n: number): Block {
  let blocks = state.field(blockState);
  return blocks[n] as Block;
}

export function getNoteBlockFromPos(state: EditorState, pos: number): Block | undefined {
  let blocks = state.field(blockState);
  return findBlockWithPos(blocks, pos);
}

export function getNoteBlocksBetween(state: EditorState, from: number, to: number): Block[] {
  return state.field(blockState).filter((block) => block.from < to && block.to >= from);
}

export function getNoteBlocksFromRangeSet(state: EditorState, ranges: any): Block[] {
  const blocks = [];
  const seenBlockStarts = new Set();
  for (const range of ranges) {
    if (!seenBlockStarts.has(range.from)) {
      blocks.push(...getNoteBlocksBetween(state, range.from, range.to));
      seenBlockStarts.add(range.from);
    }
  }
  return blocks;
}

class NoteBlockStart extends WidgetType {
  isFirst: boolean;
  constructor(isFirst: boolean) {
    super();
    this.isFirst = isFirst;
  }
  eq(other: NoteBlockStart) {
    return this.isFirst === other.isFirst;
  }
  toDOM() {
    let wrap = document.createElement("div");
    wrap.className = "heynote-block-start" + (this.isFirst ? " first" : "");
    //wrap.innerHTML = "<br>"
    return wrap;
  }
  ignoreEvent() {
    return false;
  }
}
const noteBlockWidget = () => {
  const decorate = (state: EditorState) => {
    const widgets: Range<Decoration>[] = [];

    state.field(blockState).forEach((block: Block) => {
      let deco = Decoration.replace({
        widget: new NoteBlockStart(block.from === 0 ? true : false),
        inclusive: true,
        block: true,
        side: 0,
      });
      //console.log("deco range:", block.from === 0 ? block.from : block.from+1,block.contentFrom-1)
      widgets.push(deco.range(block.from === 0 ? block.from : block.from + 1, block.contentFrom - 1));
    });

    return widgets.length > 0 ? RangeSet.of(widgets) : Decoration.none;
  };

  const noteBlockStartField = StateField.define({
    create(state: EditorState) {
      return decorate(state);
    },
    update(widgets, transaction) {
      // if widgets are empty it likely means we didn't get a parsed syntax tree, and then we want to update
      // the decorations on all updates (and not just document changes)
      if (transaction.docChanged || widgets.size === 0) {
        return decorate(transaction.state);
      }

      //return widgets.map(transaction.changes);
      return widgets;
    },
    provide(field) {
      // @ts-ignore
      return EditorView.decorations.from(field);
    },
  });

  return noteBlockStartField;
};

function atomicRanges(view: EditorView): DecorationSet {
  let builder = new RangeSetBuilder<Decoration>();
  view.state.field(blockState).forEach((block) => {
    builder.add(block.from, block.contentFrom, Decoration.mark({}));
  });
  return builder.finish();
}

const atomicNoteBlock = ViewPlugin.fromClass(
  class {
    atomicRanges: DecorationSet;
    constructor(view: EditorView) {
      this.atomicRanges = atomicRanges(view);
    }

    update(update: ViewUpdate) {
      if (update.docChanged) {
        this.atomicRanges = atomicRanges(update.view);
      }
    }
  },
  {
    provide: (plugin) =>
      EditorView.atomicRanges.of((view) => {
        return view.plugin(plugin)?.atomicRanges || Decoration.none;
      }),
  },
);

const blockLayer = layer({
  above: false,

  markers(view: EditorView) {
    const markers: RectangleMarker[] = [];
    let idx = 0;
    //console.log("visible ranges:", view.visibleRanges[0].from, view.visibleRanges[0].to, view.visibleRanges.length)
    function rangesOverlaps(range1: SimpleRange, range2: SimpleRange): boolean {
      return range1.from <= range2.to && range2.from <= range1.to;
    }
    const blocks = view.state.field(blockState);
    blocks.forEach((block) => {
      // make sure the block is visible
      if (!view.visibleRanges.some((range) => rangesOverlaps(blockContent(block), range))) {
        idx++;
        return;
      }
      const firstVisibleRange = view.visibleRanges[0];
      const lastVisibleRange = view.visibleRanges[view.visibleRanges.length - 1];
      if (!firstVisibleRange || !lastVisibleRange) {
        idx++;
        return;
      }
      const fromCoords = view.coordsAtPos(Math.max(block.contentFrom, firstVisibleRange.from));
      if (!fromCoords) {
        // this often fires during refresh in vite
        return;
      }
      const fromCoordsTop = fromCoords.top;
      const toCoords = view.coordsAtPos(Math.min(block.to, lastVisibleRange.to));
      if (!toCoords) {
        idx++;
        return;
      }
      let toCoordsBottom = toCoords.bottom;
      if (idx === blocks.length - 1) {
        // Calculate how much extra height we need to add to the last block
        let extraHeight =
          // @ts-ignore
          view.viewState.editorHeight -
          (view.defaultLineHeight + // when scrolling furthest down, one line is still shown at the top
            view.documentPadding.top +
            8);
        toCoordsBottom += extraHeight;
      }
      markers.push(
        new RectangleMarker(
          idx++ % 2 == 0 ? "block-even" : "block-odd",
          0,
          // Change "- 0 - 6" to "+ 1 - 6" on the following line, and "+ 1 + 13" to "+2 + 13" on the line below,
          // in order to make the block backgrounds to have no gap between them
          fromCoordsTop - (view.documentTop - view.documentPadding.top) - 1 - 6,
          null, // width is set to 100% in CSS
          toCoordsBottom - fromCoordsTop + 15,
        ),
      );
    });
    return markers;
  },

  update(update: ViewUpdate, dom) {
    return update.docChanged || update.viewportChanged;
  },

  class: "heynote-blocks-layer",
});

const preventFirstBlockFromBeingDeleted = EditorState.changeFilter.of((tr) => {
  //console.log("change filter!", tr)
  const protect = [];
  if (
    // @ts-ignore
    !tr.annotations.some((a) => a.type === heynoteEvent) &&
    firstBlockDelimiterSize
  ) {
    protect.push(0, firstBlockDelimiterSize);
  }
  // if the transaction is a search and replace, we want to protect all block delimiters
  if (
    // @ts-ignore
    tr.annotations.some((a) => a.value === "input.replace" || a.value === "input.replace.all")
  ) {
    const blocks = tr.startState.field(blockState);
    blocks.forEach((block) => {
      protect.push(block.from, block.contentFrom);
    });
    //console.log("protected ranges:", protect)
  }
  if (protect.length > 0) {
    return protect;
  }
  return true;
});

/**
 * Transaction filter to prevent the selection from being before the first block
 */
const preventSelectionBeforeFirstBlock = EditorState.transactionFilter.of((tr) => {
  if (!firstBlockDelimiterSize || tr.annotation(heynoteEvent)) {
    return tr;
  }
  const delimSize = firstBlockDelimiterSize;
  tr?.selection?.ranges.forEach((range) => {
    // change the selection to after the first block if the transaction sets the selection before the first block
    if (range && range.from < delimSize) {
      // @ts-ignore
      range.from = delimSize;
      //console.log("changing the from selection to", markerSize)
    }
    if (range && range.to < delimSize) {
      // @ts-ignore
      range.to = delimSize;
      //console.log("changing the from selection to", markerSize)
    }
  });
  return tr;
});

export function getBlockLineFromPos(state: EditorState, pos: number) {
  const line = state.doc.lineAt(pos);
  const block = state
    .field(blockState)
    .find((block) => block.contentFrom <= line.from && block.to >= line.from);
  if (block) {
    const firstBlockLine = state.doc.lineAt(block.contentFrom).number;
    return {
      line: line.number - firstBlockLine + 1,
      col: pos - line.from + 1,
      length: line.length,
    };
  }
  return null;
}

export const blockLineNumbers = lineNumbers({
  formatNumber(lineNo: number, state: EditorState) {
    if (state.doc.lines >= lineNo) {
      const lineInfo = getBlockLineFromPos(state, state.doc.line(lineNo).from);
      if (lineInfo !== null) {
        return `${lineInfo.line}`;
      }
    }
    return "";
  },
});

function getSelectionSize(state: EditorState, sel: any): number {
  let count = 0;
  let numBlocks = 0;
  for (const block of state.field(blockState)) {
    if (sel.from <= block.to && sel.to > block.from) {
      count += Math.min(sel.to, block.to) - Math.max(sel.from, block.contentFrom);
      numBlocks++;
    }
  }
  count += (numBlocks - 1) * 2; // add 2 for each block separator
  return count;
}

const emitCursorChange = (editor: MultiBlockEditor) =>
  ViewPlugin.fromClass(
    class {
      update(update: ViewUpdate) {
        // if the selection changed or the language changed (can happen without selection change),
        // emit a selection change event
        const langChange = update.transactions.some((tr) => tr.annotation(heynoteEvent) === LANGUAGE_CHANGE);
        if (update.selectionSet || langChange) {
          const cursorLine = getBlockLineFromPos(update.state, update.state.selection.main.head);

          const selectionSize = update.state.selection.ranges
            .map((sel) => getSelectionSize(update.state, sel))
            .reduce((a, b) => a + b, 0);

          const block = getActiveNoteBlock(update.state);
          if (block && cursorLine) {
            editor.element.dispatchEvent(
              new SelectionChangeEvent({
                cursorLine,
                selectionSize,
                language: block.language,
                languageAuto: block.autoDetect,
              }),
            );
          }
        }
      }
    },
  );

export const noteBlockExtension = (editor: MultiBlockEditor) => {
  return [
    blockState,
    noteBlockWidget(),
    atomicNoteBlock,
    blockLayer,
    preventFirstBlockFromBeingDeleted,
    preventSelectionBeforeFirstBlock,
    emitCursorChange(editor),
    mathBlock,
    emptyBlockSelected,
  ];
};

export function insertAfterActiveBlock(view: EditorView, text: string): boolean {
  const { state } = view;
  if (state.readOnly) {
    return false;
  }
  const block = getActiveNoteBlock(state);
  if (!block) return false;
  let tr = view.state.update(
    {
      changes: {
        from: block.to,
        insert: text,
      },
      selection: EditorSelection.cursor(block.to + text.length),
    },
    {
      scrollIntoView: true,
      userEvent: "input",
    },
  );
  view.dispatch(tr);
  return true;
}
